import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DirectUpload } from "@rails/activestorage";
import classNames from "classnames";
import { PropTypes } from "prop-types";
import React from "react";
import autoBind from "react-autobind";
import PhoneInput from "react-phone-input-2";

import isEmail from "validator/lib/isEmail";
import isMobilePhone from "validator/lib/isMobilePhone";
import AgentActions from "~/actions/agent_actions";
import LeadDrawerActions from "~/actions/lead_drawer_actions";
import Drawer from "~/components/drawers/drawer";
import DuplicateContactCard from "~/components/duplicate_contact_card";
import AllowOrgDuplicateCheckBox from "~/components/forms/allow_org_duplicate_checkbox";
import CurrencyInput from "~/components/forms/currency_input";
import MultipleListingServicesSelect from "~/components/forms/dropdowns/MultipleListingServicesSelect";
import Select from "~/components/forms/dropdowns/SelectWrapper/Select";
import StatusSelect from "~/components/forms/dropdowns/StatusSelect";
import {
  contactOwnerSelectValue,
  contactOwnerValue,
  NO_OWNER_OPTIONS,
  TeamMemberPicker,
  TeammatePicker,
} from "~/components/forms/dropdowns/TeamMemberSelect";
import ErrorMessage from "~/components/forms/ErrorMessage";
import ErrorMessageWithIcon from "~/components/forms/ErrorMessageWithIcon";
import { SourceSelect } from "~/components/forms/lead_fields";
import { EMAIL_TYPE_OPTIONS } from "~/constants/dropdowns/leads/EmailTypeOptions";
import { PRODUCTION_LABEL_OPTIONS } from "~/constants/dropdowns/leads/ProductionLabelOptions";
import { RATING_OPTIONS } from "~/constants/dropdowns/leads/RatingOptions";
import DrawerContext from "~/contexts/DrawerContext";
import AddLeadAdditionalDetailsSection from "~/features/customFields/AddLeadAdditionalDetailsSection";
import { LEAD_SOURCES } from "~/helpers/constants";
import LeadHelpers from "~/helpers/lead_helpers";
import brokerbit from "~/lib/brokerbit";
import { getToken } from "~/lib/tokenStore";
import { initWebSpellChecker } from "~/lib/web_spell_checker";
import LeadDrawerStore from "~/stores/lead_drawer_store";

let leadStoreListener;
let approvedForm = false;

const { currentTeam } = Rails.helpers;
const teamID = currentTeam ? currentTeam.id : null;
const uploadUrl = `${Rails.apiUrl}/rails/active_storage/direct_uploads`;
const MAX_FILE_SIZE = 26214400; // 25MB
const MAX_FILE_SIZE_IN_MB = MAX_FILE_SIZE / (1024 * 1024);
const SUPPORTED_FILE_FORMATS = ["pdf", "doc", "docx"];

class NewLeadDrawer extends React.Component {
  constructor(props) {
    super(props);

    autoBind(this);
    this.customFieldsRef = React.createRef();

    const { role } = Rails.helpers.currentUser;

    const { jobId } = props;

    this.emptyEmail = {
      id: null,
      email: "",
      email_type: "office",
      main: false,
      team_id: teamID,
      _destroy: false,
    };

    this.emptyAgentID = {
      id: null,
      agent_id: "",
      multiple_listing_service_ouid: "",
      _destroy: false,
    };

    const defaultSource = this.getDefaultValueForSource();
    const defaultReferrerId = this.getDefaultValueForReferrerId();

    this.state = {
      lead: {
        first_name: "",
        last_name: "",
        phone: "",
        agent_id: "",
        owner_id: Rails.helpers.currentUser.id,
        referrer_id: defaultReferrerId,
        production_ytd: "",
        production_label: "",
        currently_at: "",
        joined_at: "",
        note: "",
        production_ltm: "",
        source: defaultSource,
        rating: "",
        status: "new_lead",
        allow_duplicates_in_org: false,
        lead_emails_attributes: [
          {
            id: null,
            email: "",
            email_type: "office",
            main: true,
            team_id: teamID,
            _destroy: false,
          },
        ],
        lead_agent_ids_attributes: [
          {
            id: null,
            agent_id: "",
            multiple_listing_service_ouid: "",
            _destroy: false,
          },
        ],
        job_application: {
          job_id: jobId,
          cover_letter: null,
          resume: null,
          resume_doc_type: null,
          cover_letter_doc_type: null,
          resume_file_name: null,
          cover_letter_file_name: null,
        },
      },
      duplicates_found_in_org: false,
      duplicate_org_accounts: [],
      errors: {},
      warnings: {},
      uploading_resume: false,
      uploading_cover_letter: false,
      resume_uploaded: false,
      cover_letter_uploaded: false,
      resume_file_extension: null,
      cover_letter_file_extension: null,
      resume_original_file_name: null,
      cover_letter_original_file_name: null,
    };

    this.notesRef = React.createRef(null);
    this.coverLetterInputRef = React.createRef(null);
    this.resumeInputRef = React.createRef(null);
  }

  componentDidMount() {
    // listen to LeadDrawerStore changes
    leadStoreListener = LeadDrawerStore.addListener(this.onChange);
    // initialize spell checker for notes
    this.initializeSpellCheckerForNotes();
  }

  componentWillUnmount() {
    // remove listener to LeadDrawerStore changes on Unmount
    if (leadStoreListener) {
      leadStoreListener.remove();
    }
  }

  handleEmailTypeChange(opt, leadEmail) {
    leadEmail.email_type = opt ? opt.value : "";

    this.setState({ lead: this.state.lead });
  }

  handleCoverLetterClick(e) {
    e.preventDefault();

    if (this.coverLetterInputRef.current) {
      this.coverLetterInputRef.current.click();
    }
  }

  handleResumeClick(e) {
    e.preventDefault();

    if (this.resumeInputRef.current) {
      this.resumeInputRef.current.click();
    }
  }

  checkFileTypeAndSize(file, fileType = "document") {
    const extension = file.name.split(".").pop().toLowerCase();
    const isFileTypeValid = SUPPORTED_FILE_FORMATS.includes(extension);
    const isSizeWithinLimit = file.size <= MAX_FILE_SIZE;

    if (!file) {
      return null;
    }

    if (!isFileTypeValid) {
      GlobalContainer.notify(
        `Unsupported File Format: ${extension}. Supported formats: ${SUPPORTED_FILE_FORMATS.join(", ")}`,
        "error",
      );
      return null;
    }

    if (!isSizeWithinLimit) {
      GlobalContainer.notify(
        `File Size Limit Exceeded: Ensure your file is within ${MAX_FILE_SIZE_IN_MB} megabytes`,
        "error",
      );
      return null;
    }

    return extension;
  }

  uploadFileWithDirectUpload(file) {
    return new Promise((resolve, reject) => {
      const upload = new DirectUpload(file, uploadUrl, {
        directUploadWillCreateBlobWithXHR: (xhr) => {
          xhr.setRequestHeader("Authorization", `Bearer ${getToken()}`);
        },
      });

      upload.create((error, blob) => {
        if (!error) {
          resolve(blob.signed_id);
        } else {
          reject(error);
        }
      });
    });
  }

  async handleResumeChange(e) {
    const file = e.target.files[0];
    if (!file) {
      return;
    }

    const fileExtension = this.checkFileTypeAndSize(file, "resume");
    if (!fileExtension) {
      return;
    }

    this.setState({ uploading_resume: true });

    try {
      const signedId = await this.uploadFileWithDirectUpload(file);
      const { lead } = this.state;
      lead.job_application.resume = signedId;
      const resume_doc_type =
        fileExtension === "pdf" ? "pdf_resume" : "word_resume";
      lead.job_application.resume_doc_type = resume_doc_type;
      lead.job_application.resume_file_name = file.name;
      this.setState({
        lead,
        uploading_resume: false,
        resume_uploaded: true,
        resume_file_extension: fileExtension,
        resume_original_file_name: file.name,
      });
    } catch (error) {
      console.error("Resume upload failed:", error);
      GlobalContainer.notify(
        "Error uploading resume. Please try again.",
        "error",
      );
      this.setState({ uploading_resume: false });
    }
  }

  async handleCoverLetterChange(e) {
    const file = e.target.files[0];
    if (!file) {
      return;
    }

    const fileExtension = this.checkFileTypeAndSize(file, "cover_letter");
    if (!fileExtension) {
      return;
    }

    this.setState({ uploading_cover_letter: true });

    try {
      const signedId = await this.uploadFileWithDirectUpload(file);
      const { lead } = this.state;
      lead.job_application.cover_letter = signedId;
      const cover_letter_doc_type =
        fileExtension === "pdf" ? "pdf_document" : "word_document";
      lead.job_application.cover_letter_doc_type = cover_letter_doc_type;
      lead.job_application.cover_letter_file_name = file.name;
      this.setState({
        lead,
        uploading_cover_letter: false,
        cover_letter_uploaded: true,
        cover_letter_file_extension: fileExtension,
        cover_letter_original_file_name: file.name,
      });
    } catch (error) {
      console.error("Cover letter upload failed:", error);
      GlobalContainer.notify(
        "Error uploading cover letter. Please try again.",
        "error",
      );
      this.setState({ uploading_cover_letter: false });
    }
  }

  handleRemoveResume(e) {
    e.preventDefault();
    const { lead } = this.state;
    const newLeadState = {
      ...lead,
      job_application: {
        ...lead.job_application,
        resume: null,
        resume_doc_type: null,
        resume_file_name: null,
      },
    };
    this.setState({
      lead: newLeadState,
      resume_uploaded: false,
      resume_file_extension: null,
      resume_original_file_name: null,
    });
    this.resumeInputRef.current.value = null;
  }

  handleRemoveCoverLetter(e) {
    e.preventDefault();
    const { lead } = this.state;
    const newLeadState = {
      ...lead,
      job_application: {
        ...lead.job_application,
        cover_letter: null,
        cover_letter_doc_type: null,
        cover_letter_file_name: null,
      },
    };
    this.setState({
      lead: newLeadState,
      cover_letter_uploaded: false,
      cover_letter_file_extension: null,
      cover_letter_original_file_name: null,
    });
    this.coverLetterInputRef.current.value = null;
  }

  // For Candidate Leads, Source is Job Posting by default regardless of the role
  // For Non-Candidate Leads, Source is Blank by default for Internal Users and Referral for External Users
  getDefaultValueForSource() {
    const { jobId } = this.props;
    const { role } = Rails.helpers.currentUser;
    if (jobId) {
      return LEAD_SOURCES.JOB_POSTING;
    }

    return role.internal ? "" : LEAD_SOURCES.REFERRAL;
  }

  // For Candidate Leads, Referrer ID is Blank by default
  // For Non-Candidate Leads, Referrer ID is Blank for Internal Users and the current user's ID for External Users
  getDefaultValueForReferrerId() {
    const { jobId } = this.props;
    const { currentUser } = Rails.helpers;
    const { role, id: userId } = currentUser;
    if (jobId) {
      return null;
    }

    return role.internal ? null : userId;
  }

  handleAgentIdChange(e, leadAgentId) {
    e.preventDefault();

    leadAgentId.agent_id = e.target.value;

    this.setState({ lead: this.state.lead });
  }

  handleMultipleListingServiceChange(value, leadAgentId) {
    leadAgentId.multiple_listing_service_ouid = value;

    this.setState({ lead: this.state.lead });
  }

  handleEmailChange(e, leadEmail) {
    e.preventDefault();

    leadEmail.email = e.target.value;

    this.setState({ lead: this.state.lead });
  }

  handleMainChange(e, leadEmail) {
    leadEmail.main = e.target.checked;

    this.setState({ lead: this.state.lead });
  }

  handleAllowDuplicatesChange(value) {
    const { lead } = this.state;
    lead.allow_duplicates_in_org = value;
    this.setState({ lead });
  }

  handleLeadEmailDelete(e, leadEmail) {
    e.preventDefault();

    brokerbit.confirmBox({
      message: "Are you sure you want to delete this email?",
      callback: (ok) => {
        if (ok) {
          leadEmail._destroy = true;
          this.setState({ lead: this.state.lead });
        }
      },
    });
  }

  handleLeadAgentIdDelete(e, leadAgentId) {
    e.preventDefault();

    brokerbit.confirmBox({
      message: "Are you sure you want to delete this Agent ID?",
      callback: (ok) => {
        if (ok) {
          leadAgentId._destroy = true;
          this.setState({ lead: this.state.lead });
        }
      },
    });
  }

  // when LeadDrawerStore changes...
  onChange(createResult) {
    const emailErrors = {};
    const agentIDErrors = {};
    const leadDrawerState = createResult || LeadDrawerStore.getState();
    const { lead, leadDrawerStoreAction, errors } = leadDrawerState;
    const { duplicate_org_accounts } = this.state;
    const { openDrawer } = this.context;
    let duplicates_found_in_org = false;

    if (leadDrawerStoreAction === "createLeadDone") {
      openDrawer("lead", { lead });
    }

    if (leadDrawerStoreAction === "createLeadFail") {
      if (errors.duplicate_emails) {
        errors.duplicate_emails.forEach((e, i) => {
          if (e) {
            if (e.context === "org") {
              duplicate_org_accounts.push(
                `${e.match.name} - Email: ${e.value}`,
              );
              duplicates_found_in_org = true;
            }

            emailErrors[i] = {
              duplicate:
                "It looks like a contact with this email already exists.",
              context: e.context,
              match: e.match,
            };
          }
        });
      }

      if (errors.duplicate_agent_ids) {
        errors.duplicate_agent_ids.forEach((e, i) => {
          if (e) {
            if (e.context === "org") {
              duplicate_org_accounts.push(
                `${e.match.name} - Agent ID/MLS: ${e.value}`,
              );
              duplicates_found_in_org = true;
            }

            agentIDErrors[i] = {
              duplicate: "It looks like this agent id already exists.",
              context: e.context,
              match: e.match,
            };
          }
        });
      }

      this.setState({
        errors: emailErrors,
        warnings: agentIDErrors,
        submitting: false,
        duplicate_org_accounts,
        duplicates_found_in_org,
      });
    }
  }

  onSubmit(e) {
    e.preventDefault();
    if (this.state.submitting || this.state.customFieldsStatus?.blocked) {
      return;
    }

    const { lead } = this.state;
    const leadEmailAttributes = lead.lead_emails_attributes.filter(
      (x, i) => !x._destroy,
    );
    const leadAgentIdAttributes = lead.lead_agent_ids_attributes.filter(
      (x, i) => !x._destroy,
    );

    this.setState({
      submitting: true,
      duplicate_org_accounts: [],
      duplicates_found_in_org: false,
      errors: {},
      warnings: {},
    });

    if (this.validate() && !approvedForm) {
      leadEmailAttributes.forEach((leadEmail) => {
        if (!leadEmail.email) {
          leadEmail._destroy = true;
        }
      });

      leadAgentIdAttributes.forEach((leadAgentId) => {
        if (!leadAgentId.agent_id) {
          leadAgentId.multiple_listing_service_ouid = "";
        }

        if (
          !leadAgentId.agent_id &&
          !leadAgentId.multiple_listing_service_ouid
        ) {
          leadAgentId._destroy = true;
        }
      });

      if (!this.customFieldsRef.current?.submit(lead)) {
        LeadDrawerActions.createLead(lead);
      }
    } else {
      this.setState({ submitting: false });
    }
  }

  onCancel(e) {
    e.preventDefault();
    if (!this.allowCustomFieldsNavigation()) {
      return;
    }

    const { closeDrawer } = this.context;

    closeDrawer();
  }

  onCustomFieldsCreated(lead) {
    this.context.openDrawer("lead", { loadLeadID: lead.id });
    AgentActions.reloadLeadTables();
  }

  onCustomFieldsFailure(errors) {
    this.onChange({ errors, leadDrawerStoreAction: "createLeadFail" });
  }

  onCustomFieldsStateChange(customFieldsStatus) {
    this.setState({ customFieldsStatus });
  }

  allowCustomFieldsNavigation() {
    if (this.state.submitting || this.state.customFieldsStatus?.busy) {
      return false;
    }
    return (
      !this.state.customFieldsStatus?.dirty ||
      window.confirm("Discard unsaved Additional Details changes?")
    );
  }

  setFormField(name, val) {
    this.setState((prevState) => ({
      lead: {
        ...prevState.lead,
        [name]: val,
      },
    }));
  }

  initializeSpellCheckerForNotes() {
    const ele = this.notesRef.current;
    if (ele) {
      initWebSpellChecker(ele);
    }
  }

  isLeadEmailValid(index) {
    const { errors } = this.state;

    return errors[index] || errors.no_email_agent_id
      ? errors.no_email_agent_id ||
          errors[index].email ||
          errors[index].duplicate
      : "";
  }

  isLeadAgentIdValid(index) {
    const { errors, warnings } = this.state;

    return warnings[index] || errors.no_email_agent_id
      ? errors.no_email_agent_id || warnings[index].duplicate
      : "";
  }

  addLeadEmail() {
    const { lead } = this.state;

    lead.lead_emails_attributes.push({ ...this.emptyEmail });

    this.setState({ lead });
  }

  addLeadAgentId() {
    const { lead } = this.state;

    lead.lead_agent_ids_attributes.push({ ...this.emptyAgentID });

    this.setState({ lead });
  }

  validate() {
    let all_emails_blank = true;
    let all_agent_ids_blank = true;

    const { lead } = this.state;

    const leadEmailAttributes = lead.lead_emails_attributes.filter(
      (x, i) => !x._destroy,
    );
    const checkedCount = leadEmailAttributes.filter((x, i) => x.main).length;
    const leadAgentIdAttributes = lead.lead_agent_ids_attributes.filter(
      (x, i) => !x._destroy,
    );

    const errors = {};
    const warnings = {};

    if (!lead.first_name) {
      errors.first_name = "This field can not be empty";
    }

    if (!lead.last_name) {
      errors.last_name = "This field can not be empty";
    }

    if (!_lodash.isEmpty(lead.phone) && !isMobilePhone(lead.phone, ["en-US"])) {
      errors.phone = "Is invalid";
    }

    if (leadEmailAttributes.length > 0 && checkedCount < 1) {
      errors.emails = ["Please select any main email"];
    }

    leadEmailAttributes.forEach((leadEmail, index) => {
      if (leadEmail.email) {
        all_emails_blank = false;

        if (!isEmail(leadEmail.email)) {
          errors[index] = { email: "The provided email is invalid" };
        }
      }

      if (!leadEmail.email_type) {
        errors[index] = { email_type: "Can't be empty" };
      }
    });

    leadAgentIdAttributes.forEach((leadAgentId, index) => {
      if (leadAgentId.agent_id) {
        all_agent_ids_blank = false;
      }

      if (
        !!leadAgentId.agent_id &&
        !leadAgentId.multiple_listing_service_ouid
      ) {
        warnings[index] = {
          multiple_listing_service_ouid:
            "It is recommended that you select the MLS for this agent ID.",
        };
      }
    });

    if (
      (leadEmailAttributes.length === 0 &&
        leadAgentIdAttributes.length === 0) ||
      (all_emails_blank && all_agent_ids_blank)
    ) {
      errors.no_email_agent_id = [
        "Please add either an email address or agent ID to be able to create this record so that there is a way to match and update the data later.",
      ];
    }

    if (_lodash.size(errors) === 0 && _lodash.size(warnings) !== 0) {
      approvedForm = !approvedForm;
    } else {
      approvedForm = false;
    }

    this.setState({ errors, warnings });

    return _lodash.size(errors) === 0;
  }

  renderLeadEmail() {
    const { lead, errors } = this.state;
    const leadEmailAttributes = lead.lead_emails_attributes.filter(
      (x, i) => !x._destroy,
    );
    const checkedCount = leadEmailAttributes.filter((x, i) => x.main).length;
    const disableMainCheckbox = checkedCount >= 1;

    return leadEmailAttributes.map((leadEmail, index) => {
      const emailDOM = (
        <div className="form-row align-items-center mb-2" key={index}>
          <div className="col-4 col-lg-5">
            <input
              type="text"
              className={classNames("form-control", {
                "has-error": this.isLeadEmailValid(index),
              })}
              value={leadEmail.email}
              onChange={(e) => this.handleEmailChange(e, leadEmail)}
            />
          </div>

          <div className="col-4 col-lg-5 lead-emails">
            <Select
              id="email-type"
              value={leadEmail.email_type}
              options={EMAIL_TYPE_OPTIONS}
              onChange={(opt) => this.handleEmailTypeChange(opt, leadEmail)}
              placeholder="Select ..."
              isInvalid={!!errors[index]?.email_type}
              isSearchable={false}
            />
          </div>

          <div className="col-auto">
            <div className="form-check">
              <input
                key={index}
                className="form-check-input"
                type="checkbox"
                id={`main_${index}`}
                onChange={(e) => this.handleMainChange(e, leadEmail)}
                checked={leadEmail.main || false}
                disabled={!leadEmail.main && disableMainCheckbox}
              />
              <label className="form-check-label" htmlFor={`main_${index}`}>
                Main
              </label>
            </div>
          </div>

          <div className="col-auto">
            <button
              type="button"
              className="btn btn-outline-danger btn-sm"
              onClick={(e) => this.handleLeadEmailDelete(e, leadEmail)}
            >
              <FontAwesomeIcon icon={["fas", "fa-times"]} />
            </button>
          </div>

          {errors[index] && errors[index].duplicate && (
            <div className="col-12">
              <ErrorMessageWithIcon message={[errors[index].duplicate]} />
              {errors[index].context === "team" && (
                <DuplicateContactCard duplicate={errors[index]} />
              )}
            </div>
          )}
          {errors[index] && errors[index].email && (
            <div className="col-12">
              <ErrorMessageWithIcon message={errors[index].email} />
            </div>
          )}
        </div>
      );

      return emailDOM;
    });
  }

  renderLeadAgentId() {
    const { lead, warnings } = this.state;
    const leadAgentIdAttributes = lead.lead_agent_ids_attributes.filter(
      (x, i) => !x._destroy,
    );

    return leadAgentIdAttributes.map((leadAgentId, index) => {
      const agentIdDOM = (
        <div className="form-row align-items-center mb-2" key={index}>
          <div className="col-5 col-lg-5">
            <input
              type="text"
              className={classNames("form-control", {
                "has-error": this.isLeadAgentIdValid(index),
              })}
              value={leadAgentId.agent_id}
              onChange={(e) => this.handleAgentIdChange(e, leadAgentId)}
            />
          </div>

          <div
            className="col-5 col-lg-6 lead-agent-ids"
            id={`mls-select-${index}`}
          >
            <MultipleListingServicesSelect
              id={`mls-select-${index}`}
              value={leadAgentId.multiple_listing_service_ouid}
              placeholder="--- Select a MLS ---"
              onChange={(opt) =>
                this.handleMultipleListingServiceChange(
                  opt ? opt.value : "",
                  leadAgentId,
                )
              }
              teamMlses
              primaryMls
              isClearable
              isSearchable
            />
          </div>

          <div className="col-auto">
            <button
              type="button"
              className="btn btn-outline-danger btn-sm"
              onClick={(e) => this.handleLeadAgentIdDelete(e, leadAgentId)}
            >
              <FontAwesomeIcon icon={["fas", "fa-times"]} />
            </button>
          </div>

          {warnings[index] && warnings[index].multiple_listing_service_ouid && (
            <div className="col-12 text-warning mt-2">
              <FontAwesomeIcon icon={["fas", "fa-exclamation-triangle"]} />{" "}
              {warnings[index].multiple_listing_service_ouid}
            </div>
          )}

          {warnings[index] && warnings[index].duplicate && (
            <div className="col-12">
              <ErrorMessageWithIcon message={[warnings[index].duplicate]} />
              {warnings[index].context === "team" && (
                <DuplicateContactCard duplicate={warnings[index]} />
              )}
            </div>
          )}
        </div>
      );

      return agentIdDOM;
    });
  }

  render() {
    const {
      lead,
      errors,
      duplicates_found_in_org,
      duplicate_org_accounts,
      submitting,
      resume_file_extension,
      cover_letter_file_extension,
      resume_uploaded,
      cover_letter_uploaded,
      uploading_resume,
      uploading_cover_letter,
      resume_original_file_name,
      cover_letter_original_file_name,
    } = this.state;

    const { jobId } = this.props;
    const isCandidateForm = !!jobId;
    const saveButton = approvedForm ? "Approve & Save" : "Save";

    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <Drawer
          contentClass="bg-grey-lightest"
          beforeNavigate={this.allowCustomFieldsNavigation}
          navigationDisabled={submitting}
        >
          <div className="modal-header bg-white bb">
            <h5 className="modal-title">
              {isCandidateForm ? "Add Candidate" : "Add Lead"}
            </h5>
          </div>

          <div className="modal-body">
            <div className="field-group mb15">
              <label htmlFor="first_name" className="d-block">
                First Name
              </label>
              <input
                type="text"
                id="first_name"
                className={`form-control ${errors.first_name ? "has-error" : ""}`}
                onChange={(v) => {
                  this.setFormField("first_name", v.target.value);
                }}
              />
              {errors.first_name && (
                <ErrorMessageWithIcon message={[errors.first_name]} />
              )}
            </div>

            <div className="field-group mb15">
              <label htmlFor="last_name" className="d-block">
                Last Name
              </label>
              <input
                type="text"
                id="last_name"
                className={`form-control ${errors.last_name ? "has-error" : ""}`}
                onChange={(v) => {
                  this.setFormField("last_name", v.target.value);
                }}
              />
              {errors.last_name && (
                <ErrorMessageWithIcon message={[errors.last_name]} />
              )}
            </div>

            <div className="field-group mb15">
              <label htmlFor="btn-lead-email" className="d-block">
                <a
                  href="#lead_email"
                  id="btn-lead-email"
                  className="pull-right"
                  onClick={(e) => {
                    e.preventDefault();
                    this.addLeadEmail();
                  }}
                >
                  Add Email
                </a>
                Email
              </label>

              {this.renderLeadEmail()}

              {errors.emails && (
                <ErrorMessageWithIcon message={errors.emails} />
              )}

              {errors.no_email_agent_id && (
                <ErrorMessageWithIcon message={errors.no_email_agent_id} />
              )}

              {duplicates_found_in_org && (
                <AllowOrgDuplicateCheckBox
                  checked={lead.allow_duplicates_in_org}
                  duplicate_org_accounts={duplicate_org_accounts}
                  onChange={this.handleAllowDuplicatesChange}
                />
              )}
            </div>

            <div className="field-group mb15">
              <label htmlFor="phone-number" className="d-block">
                Cell Phone
              </label>
              <PhoneInput
                country="us"
                onlyCountries={["us"]}
                disableCountryCode
                disableDropdown
                placeholder=""
                defaultMask="(...) ...-...."
                value={LeadHelpers.nationalFormatPhoneNumber(lead.phone)}
                onChange={(v) => {
                  this.setFormField("phone", v);
                }}
                specialLabel={false}
                inputProps={{
                  id: "phone-number",
                  className: classNames("phone form-control", {
                    "has-error": !!errors.phone,
                  }),
                }}
              />
            </div>

            <div className="field-group mb15">
              <label htmlFor="btn-lead-agent-id" className="d-block">
                <a
                  href="#lead_agent_id"
                  id="btn-lead-agent-id"
                  className="pull-right"
                  onClick={(e) => {
                    e.preventDefault();
                    this.addLeadAgentId();
                  }}
                >
                  Add Agent ID
                </a>
                Agent ID
              </label>

              {this.renderLeadAgentId()}
            </div>

            {GlobalContainer.product() === "recruiting" && (
              <div className="field-group mb15">
                <label htmlFor="currently_at" className="d-block">
                  Currently At
                </label>
                <input
                  type="text"
                  id="currently_at"
                  className="form-control"
                  onChange={(v) => {
                    this.setFormField("currently_at", v.target.value);
                  }}
                />
              </div>
            )}

            <div className="field-group mb15">
              <label htmlFor="total_dollar_production_ltm" className="d-block">
                Total $ Production LTM
              </label>

              <div className="form-group has-feedback mb5">
                <CurrencyInput
                  id="total_dollar_production_ltm"
                  name="production_ytd"
                  value={lead.production_ytd}
                  size={9}
                  onChange={(v) => {
                    this.setFormField("production_ytd", v);
                  }}
                />
              </div>
            </div>

            <div className="field-group mb15">
              <label htmlFor="total_number_production_ltm" className="d-block">
                Total # Production LTM
              </label>

              <div className="form-group has-feedback mb5">
                <input
                  type="text"
                  id="total_number_production_ltm"
                  className="form-control"
                  onChange={(v) => {
                    this.setFormField("production_ltm", v.target.value);
                  }}
                />
              </div>
            </div>

            <div className="field-group mb15">
              <label htmlFor="production-label" className="d-block">
                Production Label
              </label>
              <Select
                id="production-label"
                value={lead.production_label}
                options={PRODUCTION_LABEL_OPTIONS}
                onChange={(opt) => {
                  this.setFormField("production_label", opt?.value ?? "");
                }}
              />
            </div>

            {Rails.abilities.manageLead && (
              <>
                <div className="field-group mb15">
                  <label htmlFor="source" className="d-block">
                    Source
                  </label>
                  <SourceSelect
                    id="source"
                    name="source"
                    isClearable
                    value={lead.source}
                    onChange={(opt) => {
                      this.setFormField("source", opt?.value ?? "");
                    }}
                  />
                </div>

                <div className="field-group mb15">
                  <label htmlFor="status" className="d-block">
                    Status
                  </label>
                  <StatusSelect
                    id="status"
                    value={lead.status}
                    stage="recruiting_with_shared"
                    onChange={(opt) =>
                      this.setFormField("status", opt?.value ?? "")
                    }
                    isInvalid={!!errors.status}
                    isSearchable
                  />

                  {errors.status && <ErrorMessage message={errors.status} />}
                </div>

                <div className="field-group mb15">
                  <label htmlFor="rating">Rating</label>
                  <Select
                    id="rating"
                    value={lead.rating}
                    options={RATING_OPTIONS}
                    placeholder="Select Ratings"
                    onChange={(opt) =>
                      this.setFormField("rating", opt?.value ?? "")
                    }
                    isClearable
                    isSearchable
                  />
                </div>
              </>
            )}

            {Rails.abilities.manageLead &&
              GlobalContainer.product() === "recruiting" && (
                <div className="field-group mb15">
                  <TeammatePicker
                    value={contactOwnerSelectValue(lead.owner_id)}
                    label="Owner"
                    additionalOptions={NO_OWNER_OPTIONS}
                    onChange={(opt) => {
                      this.setFormField("owner_id", contactOwnerValue(opt));
                    }}
                  />
                </div>
              )}

            {Rails.abilities.manageLead &&
              GlobalContainer.product() === "recruiting" && (
                <div className="field-group mb15">
                  <TeamMemberPicker
                    value={lead.referrer_id}
                    addable={{ title: "Add Referrer" }}
                    label="Referrer"
                    onChange={(opt) => {
                      this.setFormField("referrer_id", opt?.value ?? "");
                    }}
                  />
                </div>
              )}

            <div className="field-group mb15">
              <label htmlFor="note" className="d-block">
                Notes
              </label>
              {isCandidateForm && (
                <>
                  {!cover_letter_uploaded && (
                    <>
                      {uploading_cover_letter ? (
                        <span className="btn btn-link pull-right p-0 pl-2 disabled">
                          <FontAwesomeIcon
                            icon="far fa-spinner"
                            pulse
                            className="mr-1"
                          />
                          Uploading Cover Letter...
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-link pull-right p-0 pl-2"
                          onClick={this.handleCoverLetterClick}
                        >
                          Add Cover Letter
                        </button>
                      )}
                    </>
                  )}

                  {!resume_uploaded && (
                    <>
                      {uploading_resume ? (
                        <span className="btn btn-link pull-right p-0 disabled">
                          <FontAwesomeIcon
                            icon="far fa-spinner"
                            pulse
                            className="mr-1"
                          />
                          Uploading Resume...
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-link pull-right p-0"
                          onClick={this.handleResumeClick}
                        >
                          Add Resume
                        </button>
                      )}
                    </>
                  )}

                  <input
                    type="file"
                    ref={this.coverLetterInputRef}
                    onChange={this.handleCoverLetterChange}
                    accept="*/*"
                    style={{ display: "none" }}
                  />
                  <input
                    type="file"
                    ref={this.resumeInputRef}
                    onChange={this.handleResumeChange}
                    accept="*/*"
                    style={{ display: "none" }}
                  />
                </>
              )}
              <textarea
                id="notes"
                className="form-control"
                rows="8"
                onChange={(v) => {
                  this.setFormField("note", v.target.value);
                }}
                ref={this.notesRef}
              ></textarea>

              {isCandidateForm &&
                (resume_uploaded || cover_letter_uploaded) && (
                  <div className="mt-2">
                    {resume_uploaded && (
                      <span className="badge bg-grey-light float-right mr-2 d-inline-flex align-items-center">
                        <FontAwesomeIcon
                          icon={["fas", "fa-file"]}
                          className="mr-1"
                        />
                        {resume_original_file_name ||
                          `Resume.${resume_file_extension}`}
                        <button
                          type="button"
                          className="btn btn-sm p-0 ml-2 text-black"
                          onClick={this.handleRemoveResume}
                          style={{ fontSize: "12px", lineHeight: 1 }}
                        >
                          <FontAwesomeIcon icon={["fas", "fa-times"]} />
                        </button>
                      </span>
                    )}
                    {cover_letter_uploaded && (
                      <span className="badge bg-grey-light float-right mr-2 d-inline-flex align-items-center">
                        <FontAwesomeIcon
                          icon={["fas", "fa-file"]}
                          className="mr-1"
                        />
                        {cover_letter_original_file_name ||
                          `Cover Letter.${cover_letter_file_extension}`}
                        <button
                          type="button"
                          className="btn btn-sm p-0 ml-2 text-black"
                          onClick={this.handleRemoveCoverLetter}
                          style={{ fontSize: "12px", lineHeight: 1 }}
                        >
                          <FontAwesomeIcon icon={["fas", "fa-times"]} />
                        </button>
                      </span>
                    )}
                  </div>
                )}
            </div>
          </div>

          <div className="px-3 pb-3">
            <AddLeadAdditionalDetailsSection
              key={Rails.helpers.currentTeam.id}
              ref={this.customFieldsRef}
              teamId={Rails.helpers.currentTeam.id}
              onCreated={this.onCustomFieldsCreated}
              onFailure={this.onCustomFieldsFailure}
              onStateChange={this.onCustomFieldsStateChange}
            />
          </div>
          <div
            className="modal-footer bg-white bt"
            style={{ position: "sticky", bottom: 0, zIndex: 2 }}
          >
            <a
              href="#cancel"
              className="text-grey mr10"
              onClick={this.onCancel}
            >
              Cancel
            </a>
            {submitting ? (
              <a href="#save" className="btn btn-success btn-wide disabled">
                <FontAwesomeIcon icon="far fa-spinner" pulse className="mr5" />
                Saving ...
              </a>
            ) : (
              <a
                href="#save"
                className={classNames("btn btn-success btn-wide", {
                  disabled:
                    uploading_resume ||
                    uploading_cover_letter ||
                    this.state.customFieldsStatus?.blocked,
                })}
                onClick={this.onSubmit}
              >
                {saveButton}
              </a>
            )}
          </div>
        </Drawer>
      </form>
    );
  }
}

NewLeadDrawer.contextType = DrawerContext;

NewLeadDrawer.propTypes = {
  jobId: PropTypes.number,
};

NewLeadDrawer.defaultProps = {
  jobId: null,
};

export default NewLeadDrawer;
